/**
 * Edit Mode Toolbar Component
 * 
 * Toolbar with Save/Cancel actions for floor plan editing
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/EditModeToolbar
 */

import React from 'react';
import { Group, Button, Badge, ActionIcon, Tooltip, Stack, Text, Collapse, Box } from '@mantine/core';
import { IconDeviceFloppy, IconX, IconArrowBack, IconAlertTriangle, IconEdit, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

/**
 * Edit mode toolbar props
 */
interface EditModeToolbarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  overlapCount: number;
  onSave: () => void;
  onCancel: () => void;
  onUndo: () => void;
  disabled?: boolean;
  modalOpen?: boolean; // Hide toolbar when modals are open
}

/**
 * Edit Mode Toolbar Component
 * 
 * Displays save/cancel actions and unsaved changes indicator
 * Shows overlap warnings and provides undo functionality
 * 
 * @param props - Component props
 * @returns React component
 */
export const EditModeToolbar: React.FC<EditModeToolbarProps> = ({
  collapsed,
  onToggleCollapse,
  hasUnsavedChanges,
  isSaving,
  overlapCount,
  onSave,
  onCancel,
  onUndo,
  disabled = false,
  modalOpen = false
}) => {
  // Hide toolbar when modal is open
  if (modalOpen) {
    return null;
  }

  return (
    <Box
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 1100,
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.2s ease'
      }}
    >
      {/* Collapsed State - Enhanced Circular Button */}
      {collapsed ? (
        <Tooltip label="Edit Layout Mode" position="left" withArrow>
          <Box
            onClick={onToggleCollapse}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: '#2563EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
              transition: 'all 0.3s ease',
            }}
          >
            <IconEdit size={24} color="white" />
            {hasUnsavedChanges && (
              <Box
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#EAB308',
                  border: '3px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse-warning 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  boxShadow: '0 0 0 4px rgba(234, 179, 8, 0.2)',
                  zIndex: 1
                }}
              >
                <Text size="xs" fw={700} c="white">!</Text>
              </Box>
            )}
          </Box>
        </Tooltip>
      ) : (
        /* Expanded State - Full Toolbar */
        <Stack
          gap="sm"
          style={{
            padding: '16px',
            minWidth: '200px'
          }}
        >
          {/* Header with Toggle */}
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600} c="#1E293B">
              Edit Layout Mode
            </Text>
            <Group gap="xs">
              <Badge color={hasUnsavedChanges ? 'yellow' : 'green'} variant="dot">
                {hasUnsavedChanges ? 'Unsaved' : 'Saved'}
              </Badge>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onToggleCollapse}
              >
                <IconChevronUp size={16} />
              </ActionIcon>
            </Group>
          </Group>

          {/* Overlap Warning */}
          {overlapCount > 0 && (
            <Group gap="xs" style={{ padding: '8px', backgroundColor: '#FEF2F2', borderRadius: '4px' }}>
              <IconAlertTriangle size={16} color="#DC2626" />
              <Text size="xs" c="#DC2626" fw={500}>
                {overlapCount} overlap{overlapCount > 1 ? 's' : ''} detected
              </Text>
            </Group>
          )}

          {/* Action Buttons */}
          <Group gap="xs">
            {/* Undo Button */}
            <Tooltip label="Undo all changes" position="bottom" withArrow>
              <ActionIcon
                variant="light"
                color="gray"
                size="lg"
                onClick={onUndo}
                disabled={!hasUnsavedChanges || isSaving}
              >
                <IconArrowBack size={18} />
              </ActionIcon>
            </Tooltip>

            {/* Cancel Button */}
            <Button
              variant="light"
              color="red"
              size="sm"
              leftSection={<IconX size={16} />}
              onClick={onCancel}
              disabled={isSaving}
              fullWidth
            >
              Cancel
            </Button>

            {/* Save Button */}
            <Button
              variant="filled"
              color={overlapCount > 0 ? 'red' : 'blue'}
              size="sm"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={onSave}
              loading={isSaving}
              disabled={!hasUnsavedChanges || overlapCount > 0 || disabled}
              fullWidth
            >
              {overlapCount > 0 ? 'Fix Overlaps' : 'Save Layout'}
            </Button>
          </Group>

          {/* Instructions */}
          <Text size="xs" c="dimmed" style={{ marginTop: '8px', lineHeight: 1.4 }}>
            • Click to select room<br />
            • Drag to move position<br />
            • Drag corners to resize<br />
            • Press Esc to deselect
          </Text>
        </Stack>
      )}
    </Box>
  );
};

